import axios from 'axios';
import { API_BASE_URL } from './api';

/**
 * The operator console's API client.
 *
 * Separate from `api.js` on purpose. That client carries a tenant user's
 * token, refreshes it against /auth/refresh, and clears the tenant auth
 * store on a 401 — none of which applies to a platform operator, whose token
 * has its own audience, no refresh, and its own store. Sharing the instance
 * would mean a platform 401 logging a tenant user out, or a tenant refresh
 * overwriting the operator's Authorization header.
 *
 * The base URL is the same: /api/platform is mounted before tenant
 * resolution, so it answers on the plain API host the console reaches from
 * `manage.<portal domain>` (a reserved label, so no tenant is derived).
 */
const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

let onUnauthorized = null;

client.setToken = (token) => {
  if (token) client.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete client.defaults.headers.common.Authorization;
};

/** Called on any 401 after login, so the store can drop a dead token. */
client.onUnauthorized = (fn) => {
  onUnauthorized = fn;
};

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLogin = error.config?.url?.includes('/platform/auth/login');
    if (error.response?.status === 401 && !isLogin && onUnauthorized) onUnauthorized();
    return Promise.reject(error);
  }
);

export const platformAPI = {
  login: (email, password) => client.post('/platform/auth/login', { email, password }),
  logout: () => client.post('/platform/auth/logout'),
  me: () => client.get('/platform/auth/me'),

  // The platform's own setup checklist. `live` is the only thing that makes
  // the server reach a vendor (Cloudflare, DNS); the default never does.
  platformSetup: ({ live = false } = {}) => client.get('/platform/setup', { params: live ? { live: 1 } : undefined }),

  listTenants: () => client.get('/platform/tenants'),
  getTenant: (slug) => client.get(`/platform/tenants/${encodeURIComponent(slug)}`),
  provision: (body) => client.post('/platform/tenants', body),
  suspend: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/suspend`),
  reactivate: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/reactivate`),
  migrate: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/migrate`),
  diagnostics: (slug) => client.get(`/platform/tenants/${encodeURIComponent(slug)}/diagnostics`),
  invite: (slug, body) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/invite`, body),

  // Phase 6: export/restore, staged delete, sandbox, isolation, usage, setup, banners.
  // The export is a download with the token in a header, so it comes back
  // as a blob and the page hands it to the browser itself.
  exportDump: (slug) =>
    client.get(`/platform/tenants/${encodeURIComponent(slug)}/export`, { responseType: 'blob', timeout: 10 * 60 * 1000 }),
  restore: (slug, file, name) =>
    client.post(`/platform/tenants/${encodeURIComponent(slug)}/restore`, file, {
      params: name ? { name } : undefined,
      headers: { 'Content-Type': 'application/octet-stream' },
      timeout: 10 * 60 * 1000,
    }),
  scheduleDelete: (slug, body = {}) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/delete`, body),
  cancelDelete: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/cancel-delete`),
  createSandbox: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/sandbox`, undefined, { timeout: 10 * 60 * 1000 }),
  deleteSandbox: (slug) => client.delete(`/platform/tenants/${encodeURIComponent(slug)}/sandbox`),
  isolationCheck: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/isolation-check`),
  provisionIncludedEmail: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/email/included`),
  usage: (slug, days = 30) => client.get(`/platform/tenants/${encodeURIComponent(slug)}/usage`, { params: { days } }),
  usageRollup: (slug) => client.post(`/platform/tenants/${encodeURIComponent(slug)}/usage/rollup`),
  setup: (slug) => client.get(`/platform/tenants/${encodeURIComponent(slug)}/setup`),
  // Phase 7a: the catalogue (plans, modules, add-ons — never prices) and a
  // tenant's plan, add-ons, trial date and payments fee.
  catalogue: () => client.get('/platform/catalogue'),
  setPlan: (slug, body) => client.patch(`/platform/tenants/${encodeURIComponent(slug)}/plan`, body),
  listBanners: () => client.get('/platform/banners'),
  createBanner: (body) => client.post('/platform/banners', body),
  deleteBanner: (id) => client.delete(`/platform/banners/${encodeURIComponent(id)}`),
  // Releases: the server dispatches GitHub workflows; nothing deploys in the API itself.
  releases: () => client.get('/platform/releases'),
  releaseRun: (id) => client.get(`/platform/releases/runs/${encodeURIComponent(id)}`),
  startRelease: (kind, body = {}) => client.post(`/platform/releases/${encodeURIComponent(kind)}`, body),
};

export default client;
